/**
 * Per-user set of favorited indicators, persisted to localStorage so the
 * picker can surface a "Favorites" shortlist across charts and sessions.
 *
 * Intentionally local-only — mirrors {@link ./indicatorUserDefaults}. If we
 * ever want server-side sync, this read/write surface is the seam to swap out.
 */

const STORAGE_KEY = 'edge.chart.indicatorFavorites.v1'

function readStore(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string')
    }
  } catch {
    /* corrupted — drop it silently and start fresh */
  }
  return []
}

function writeStore(next: string[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* quota or denied — best effort, no-op */
  }
}

export function getIndicatorFavorites(): string[] {
  return readStore()
}

export function isIndicatorFavorite(name: string): boolean {
  return readStore().includes(name)
}

/** Toggle a favorite and return the resulting list. */
export function toggleIndicatorFavorite(name: string): string[] {
  const store = readStore()
  const idx = store.indexOf(name)
  const next = idx === -1 ? [...store, name] : store.filter((n) => n !== name)
  writeStore(next)
  return next
}
